import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Session, User } from '@supabase/supabase-js'
import Dexie from 'dexie'

import {
  getSupabaseCachedUser,
  supabaseAuthStorageKey,
  supabaseClient,
} from '@/clients/supabase-client'

interface AuthContextType {
  session: Session | null
  user?: User | null
  loading: boolean
  logout: typeof supabaseClient.auth.signOut
}

export const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  logout: supabaseClient.auth.signOut,
})

function toggleAutoRefresh(session?: Session | null) {
  return session
    ? supabaseClient.auth.startAutoRefresh()
    : supabaseClient.auth.stopAutoRefresh()
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null | undefined>(null)
  const [loading, setLoading] = useState(true)

  // initialize user from local cache
  useEffect(() => {
    setUser(getSupabaseCachedUser())
  }, [])

  useEffect(() => {
    supabaseClient.auth
      .getSession()
      .then((data) => {
        if (data.error) return

        const {
          data: { session },
        } = data
        setSession(session)
        setUser(session?.user ?? null)
        toggleAutoRefresh(session)
      })
      .finally(() => {
        setLoading(false)
      })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)

      toggleAutoRefresh(session)
    })

    return () => subscription.unsubscribe()
  }, [])

  const cleanupLocalData = useCallback(async () => {
    if (!user?.id) return

    try {
      await Dexie.delete(`idromeccanicasrl-gestionale-${user.id}`)
    } catch (dexieError) {
      console.error(
        'Failed to remove local data for user during logout',
        dexieError
      )
    }
  }, [user?.id])

  const clearClientSessionState = useCallback(() => {
    setSession(null)
    setUser(null)
    toggleAutoRefresh(null)
  }, [setSession, setUser])

  const removeCachedSession = useCallback(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(supabaseAuthStorageKey)
      }
    } catch (storageError) {
      console.error(
        'Failed to clear cached session during logout fallback',
        storageError
      )
    }
  }, [])

  const finalizeLogout = useCallback(async () => {
    clearClientSessionState()
    await cleanupLocalData()
  }, [cleanupLocalData, clearClientSessionState])

  const runLocalSignOut = useCallback(async () => {
    // `_removeSession` is private on the public types, so access it via an explicit cast.
    const authWithInternals = supabaseClient.auth as unknown as {
      _removeSession?: () => Promise<void>
    }

    if (typeof authWithInternals._removeSession === 'function') {
      await authWithInternals._removeSession()
    } else {
      removeCachedSession()
    }

    await finalizeLogout()
  }, [finalizeLogout, removeCachedSession])

  const logout = useCallback(async () => {
    try {
      const result = await supabaseClient.auth.signOut()

      if (result.error) {
        console.warn(
          'Remote logout returned an error, falling back to local logout',
          result.error
        )
        await runLocalSignOut()
        return { error: null }
      }

      await finalizeLogout()
      return result
    } catch (error) {
      console.warn(
        'Remote logout failed, falling back to local logout',
        error
      )
      await runLocalSignOut()
      return { error: null }
    }
  }, [finalizeLogout, runLocalSignOut])

  const value = useMemo(
    () => ({
      session,
      user,
      loading,
      logout,
    }),
    [loading, logout, session, user]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
